import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import LiveMark from "@/pages/idevra/LiveMark";

const BOOT_MS = 4200;

export default function BootSequence({ onComplete }) {
  const [stage, setStage] = useState("gate");
  const [lineCount, setLineCount] = useState(0);
  const doneRef = useRef(false);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    sfx.whoosh();
    onComplete();
  };

  const start = () => {
    if (stage !== "gate") return;
    sfx.unlock();
    sfx.boot();
    setStage("boot");
  };

  useEffect(() => {
    const onKey = (e) => {
      if (stage === "gate") start();
      else if (e.key === "Escape" || e.key === " ") finish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    if (stage !== "boot") return;
    const iv = setInterval(
      () => setLineCount((c) => Math.min(c + 1, IDEVRA.bootLines.length)),
      340
    );
    const t = setTimeout(finish, BOOT_MS);
    return () => {
      clearInterval(iv);
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  return (
    <motion.div
      className="iv-boot flex flex-col"
      data-testid="boot-sequence"
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      {stage === "gate" ? (
        <button
          type="button"
          data-testid="boot-press-start"
          onClick={start}
          className="flex flex-1 flex-col items-center justify-center gap-10 outline-none"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
          >
            <LiveMark className="w-24 sm:w-28" />
          </motion.div>
          <div className="flex flex-col items-center gap-4">
            <span className="iv-press-start f-syncopate text-[13px] sm:text-sm tracking-[0.5em] text-[#0F172A]">
              PRESS START
            </span>
            <span className="f-mono text-[10px] tracking-[0.25em] text-[#94A3B8]">
              CLICK OR PRESS ANY KEY · AUDIO ON
            </span>
          </div>
        </button>
      ) : (
        <div className="relative flex flex-1 flex-col">
          <div className="absolute left-6 top-6 sm:left-10 sm:top-10 space-y-1.5">
            {IDEVRA.bootLines.slice(0, lineCount).map((line) => (
              <motion.div
                key={line}
                className="iv-boot-line"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className="ok">▸ </span>
                {line}
              </motion.div>
            ))}
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-12">
            <div className="relative flex items-center justify-center">
              {[0, 1].map((i) => (
                <motion.span
                  key={i}
                  className="iv-ring"
                  initial={{ width: 130, height: 130, opacity: 0.55 }}
                  animate={{ width: 300, height: 300, opacity: 0 }}
                  transition={{
                    duration: 2.2,
                    delay: 0.6 + i * 1.1,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />
              ))}
              <motion.div
                initial={{ opacity: 0, scale: 0.7, filter: "blur(14px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <LiveMark className="w-28 sm:w-36" />
              </motion.div>
            </div>

            <motion.div
              className="f-syncopate text-center text-sm sm:text-base tracking-[0.55em] text-[#0F172A]"
              initial={{ opacity: 0, letterSpacing: "1.1em" }}
              animate={{ opacity: 1, letterSpacing: "0.55em" }}
              transition={{ duration: 1.4, delay: 0.7 }}
            >
              IDEVRA
            </motion.div>

            <div className="w-56 sm:w-72">
              <div className="iv-progress-track">
                <motion.div
                  className="iv-progress-fill"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: BOOT_MS / 1000 - 0.4, ease: "easeInOut" }}
                />
              </div>
              <div className="mt-3 flex justify-between f-mono text-[9px] tracking-[0.3em] text-[#94A3B8]">
                <span>INITIALIZING IP ENGINE</span>
                <span>v1.07</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            data-testid="boot-skip-btn"
            onClick={finish}
            className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 f-mono text-[10px] tracking-[0.3em] text-[#94A3B8] hover:text-[#0284C7] transition-colors"
          >
            SKIP ▸▸
          </button>
        </div>
      )}
    </motion.div>
  );
}
